
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { google } from 'google-maps';
import { getModule } from 'vuex-module-decorators';
import PathStore from '../../store/modules/paths';
import { SimplePath } from '@/flightpath-components/models/SimplePath';
import { SimplePoint } from '@/flightpath-components/models/SimplePoint';
import { Point } from '@/flightpath-components/models/Point';
import { GpsHelper } from '@/flightpath-components/services/GpsHelper';
import RenderColors from '@/flightpath-components/services/colors';
import { RenderHelper } from '@/services/RenderHelper';

@Component
export default class GoogleMapRenderPath extends Vue {
  @Prop() googleApi!: google;
  @Prop() map!: google.maps.Map<HTMLDivElement>;

  public pathStore = getModule(PathStore, this.$store);
  //We recenter when we receive a new path
  lastPathId = null as string | null;
  line!: google.maps.Polyline;
  polygon!: google.maps.Polygon;
  workArea: google.maps.Polygon | null = null;
  workAreaEditable = false;
  cropArea: google.maps.Polygon | null = null;
  cropAreaEditable = false;
  lineListenerHandle: google.maps.MapsEventListener | null = null;
  lineDragListenerHandle: google.maps.MapsEventListener | null = null;
  mapClickListenerHandle: google.maps.MapsEventListener | null = null;
  polygonClickListenerHandle: google.maps.MapsEventListener | null = null;
  workAreaClickListenerHandle: google.maps.MapsEventListener | null = null;
  workAreaDragListenerHandle: google.maps.MapsEventListener | null = null;
  cropAreaClickListenerHandle: google.maps.MapsEventListener | null = null;
  cropAreaDragListenerHandle: google.maps.MapsEventListener | null = null;
  startMarker: google.maps.Marker | null = null;
  endMarker: google.maps.Marker | null = null;

  async mounted() {
    this.addMapClickListener();
    this.createNewPolygon([]);
  }

  destroyed() {
    this.destroyResources();
  }

  deactivated() {
    this.destroyResources();
  }

  private destroyResources() {
    this.lineListenerHandle?.remove();
    this.lineDragListenerHandle?.remove();
    this.mapClickListenerHandle?.remove();
    this.polygonClickListenerHandle?.remove();
    this.workAreaClickListenerHandle?.remove();
    this.workAreaDragListenerHandle?.remove();
    this.line?.setMap(null);
    this.polygon?.setMap(null);
    this.workArea?.setMap(null);
    this.cropArea?.setMap(null);
    this.startMarker?.setMap(null);
    this.endMarker?.setMap(null);
  }

  @Watch('pathStore.selectedPath')
  onSelectedPathChange(path: SimplePath): void {
    if (this.pathStore.isSelectedPathSimplified) return;
    this.renderPath(path);
  }

  @Watch('pathStore.selectedSimplifiedPath')
  onSelectedSimplifiedPathChanged(path: SimplePath): void {
    if (!this.pathStore.isSelectedPathSimplified) return;
    this.renderPath(path);
  }

  @Watch('pathStore.editable')
  onEditableChange(): void {
    if (this.line) {
      this.line.setEditable(this.pathStore.edit);
      this.updateLineMarkers();
    }
  }

  @Watch('pathStore.editWorkArea')
  onEditWorkAreaChange(value: boolean): void {
    this.workAreaEditable = value;
    if (this.line) {
      this.line.setEditable(!value && !this.cropAreaEditable);
    }
    if (this.workArea) {
      this.workArea.setEditable(value);
      this.workArea.setOptions({ strokeColor: value ? RenderColors.WorkArea : 'transparent' });
    }
  }

  @Watch('pathStore.editCropArea')
  onEditCropAreaChange(value: boolean): void {
    this.cropAreaEditable = value;
    if (this.line) {
      this.line.setEditable(!value && !this.workAreaEditable);
    }
    if (this.cropArea) {
      this.cropArea.setEditable(value);
      this.cropArea.setOptions({ strokeColor: value ? RenderColors.CropArea : 'transparent' });
    }
  }

  @Watch('pathStore.pathAssociatedField')
  onPathAssociatedFieldChange(): void {
    this.polygon.setMap(null);
    this.createNewPolygon(this.pathStore.associatedField.BoundaryPoints);
  }

  @Watch('pathStore.clearMap')
  onClearMapChange(): void {
    if (this.line) this.line.setMap(null);
    if (this.polygon) this.polygon.setMap(null);
    if (this.startMarker) this.startMarker.setMap(null);
    if (this.endMarker) this.endMarker.setMap(null);
  }

  @Watch('pathStore.addPath')
  onAddPathChange(value: boolean): void {
    if (value) {
      if (this.line) {
        this.line.setPath([]);
        this.line.setMap(this.map);
        this.pathStore.updateCurrentLine(this.line);
      }
    }
  }

  @Watch('pathStore.revertPath')
  onRevertPathChange(): void {
    this.line.setMap(null);
    this.createNewPolyline(this.pathStore.selectedPath?.Path as SimplePoint[]);
    this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
    this.pathStore.updateCurrentLine(this.line);
  }

  @Watch('pathStore.revertWorkArea')
  onRevertWorkAreaChange(): void {
    if (!this.workArea) return;
    this.workArea.setMap(null);
    this.createNewWorkAreaPolygon(this.pathStore.currentWorkArea.map((value) => ({ Lat: value.lat(), Long: value.lng(), Alt:0 })));
  }

  @Watch('pathStore.deleteWorkArea')
  onDeleteWorkAreaChange() {
    if (!this.workArea) return;
    this.workArea.setMap(null);
    this.createNewWorkAreaPolygon([]);
  }

  @Watch('pathStore.revertCropArea')
  onRevertCropAreaChange(): void {
    if (!this.cropArea) return;
    this.cropArea.setMap(null);
    this.createNewCropAreaPolygon(this.pathStore.currentCropArea.map((value) => ({ Lat: value.lat(), Long: value.lng(), Alt:0 })));
  }

  @Watch('pathStore.deleteCropArea')
  onDeleteCropAreaChange() {
    if (!this.cropArea) return;
    this.cropArea.setMap(null);
    this.createNewCropAreaPolygon([]);
  }

  createNewPolyline(path: SimplePoint[]) {
    this.line = new this.googleApi.maps.Polyline(RenderHelper.getPathPolylineOptionsFromSimplePoint(path));
    this.line.setOptions({ zIndex: 100 });
    this.line.setMap(this.map);
    this.line.setEditable(this.pathStore.editable);

    this.addLineClickListener();
    this.addLineDragListener();
  }

  createNewPolygon(boundary: Point[]) {
    this.polygon = new this.googleApi.maps.Polygon(RenderHelper.getFieldPolygonOptionsFromPoint(boundary));
    this.polygon.setOptions({ zIndex: 0 });
    this.polygon.setMap(this.map);

    this.addPolygonClickListener();
  }

  createNewWorkAreaPolygon(boundary: SimplePoint[]) {
    if (this.workArea) this.workArea.setMap(null);
    this.workArea = new this.googleApi.maps.Polygon(RenderHelper.getWorkAreaPolygonOptionsFromSimplePoint(boundary));
    this.workArea.setMap(this.map);

    this.updateWorkArea();
    this.addWorkAreaClickListener();
    this.addWorkAreaDragListener();
  }

  createNewCropAreaPolygon(boundary: SimplePoint[]) {
    if (this.cropArea) this.cropArea.setMap(null);
    this.cropArea = new this.googleApi.maps.Polygon(RenderHelper.getCropAreaPolygonOptionsFromSimplePoint(boundary));
    this.cropArea.setMap(this.map);

    this.updateCropArea();
    this.addCropAreaClickListener();
    this.addCropAreaDragListener();
  }

  addLineClickListener() {
    if (!this.line) return;

    if (this.lineListenerHandle != null) {
      this.lineListenerHandle.remove();
    }

    this.lineListenerHandle = this.line.addListener('click', (e) => {
      if (!this.pathStore.editable) return;

      const latLong = e.latLng as google.maps.LatLng;
      const newPath = this.line.getPath();

      if (newPath.getLength() == 1) return;

      if (e.vertex != undefined) {
        newPath.removeAt(e.vertex);
        this.line.setPath(newPath);
        this.updateLineMarkers();
        this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
        this.pathStore.updateCurrentLine(this.line);
      }
    });
  }

  addLineDragListener() {
    if (!this.line) return;

    if (this.lineDragListenerHandle != null) {
      this.lineDragListenerHandle.remove();
    }

    this.lineDragListenerHandle = this.line.addListener('mouseup', () => {
      this.updateLineMarkers();
      this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
      this.pathStore.updateCurrentLine(this.line);
    });
  }

  addMapClickListener() {
    if (this.mapClickListenerHandle != null) {
      this.mapClickListenerHandle.remove();
    }

    this.mapClickListenerHandle = this.map.addListener('click', (e) => {
      this.mapClickHandler(e);
    });
  }

  addPolygonClickListener() {
    if (this.polygonClickListenerHandle != null) {
      this.polygonClickListenerHandle.remove();
    }

    this.polygonClickListenerHandle = this.polygon.addListener('click', (e) => {
      this.mapClickHandler(e);
    });
  }

  addWorkAreaClickListener() {
    if (this.workAreaClickListenerHandle != null) {
      this.workAreaClickListenerHandle.remove();
    }

    if (this.workArea) {
      this.workAreaClickListenerHandle = this.workArea.addListener('click', (e) => {
        if (e.vertex != undefined && this.pathStore.editWorkArea) {
          this.deletePointFromPath(e.vertex);
        } else {
          this.mapClickHandler(e);
        }
      });
    }
  }

  addWorkAreaDragListener() {
    if (!this.workArea) return;

    if (this.workAreaDragListenerHandle != null) {
      this.workAreaDragListenerHandle.remove();
    }

    this.workAreaDragListenerHandle = this.workArea.addListener('mouseup', () => {
      this.updateWorkArea();
    });
  }

  addCropAreaClickListener() {
    if (this.cropAreaClickListenerHandle != null) {
      this.cropAreaClickListenerHandle.remove();
    }

    if (this.cropArea) {
      this.cropAreaClickListenerHandle = this.cropArea.addListener('click', (e) => {
        if (e.vertex != undefined && this.pathStore.editCropArea) {
          this.deletePointFromPath(e.vertex);
        } else {
          this.mapClickHandler(e);
        }
      });
    }
  }

  addCropAreaDragListener() {
    if (!this.cropArea) return;

    if (this.cropAreaDragListenerHandle != null) {
      this.cropAreaDragListenerHandle.remove();
    }

    this.cropAreaDragListenerHandle = this.cropArea.addListener('mouseup', () => {
      this.updateCropArea();
    });
  }

  mapClickHandler(e: google.maps.MouseEvent) {
    if (!this.pathStore.editable) return;

    if (this.pathStore.editWorkArea) {
      this.updateWorkAreaPolygon(e);
    } else if(this.pathStore.editCropArea) {
      this.updateCropAreaPolygon(e);
    }else {
      this.updateLine(e);
    }
  }

  renderPath(path: SimplePath) {
    if (path.WorkAreas != undefined && path.WorkAreas.length > 0) {
      const array = path.WorkAreas[0].map((value: SimplePoint) => new this.googleApi.maps.LatLng(value.Lat, value.Long));
      this.pathStore.setCurrentWorkArea(array);
      const area = this.googleApi.maps.geometry.spherical.computeArea(array);
      this.pathStore.setSelectedWorkAreaArea(Math.round(area));
      this.createNewWorkAreaPolygon(path.WorkAreas[0]);
    } else {
      this.createNewWorkAreaPolygon([]);
    }

    if (path.CropAreas != undefined && path.CropAreas.length > 0) {
      const array = path.CropAreas[0].map((value: SimplePoint) => new this.googleApi.maps.LatLng(value.Lat, value.Long));
      this.pathStore.setCurrentCropArea(array);
      const area = this.googleApi.maps.geometry.spherical.computeArea(array);
      this.pathStore.setSelectedCropAreaArea(Math.round(area));
      this.createNewCropAreaPolygon(path.CropAreas[0]);
    } else {
      this.createNewCropAreaPolygon([]);
    }

    if (this.line) this.line.setMap(null);
    if (!path) return;
    this.createNewPolyline(path.Path);
    if (this.line.getPath().getLength() != 0 && path.PathID !== this.lastPathId) {
      this.map.fitBounds(RenderHelper.getBounds(this.line.getPath()));
      this.lastPathId = path.PathID;
    }

    this.updateLineMarkers();
    this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
    this.pathStore.updateCurrentLine(this.line);
  }

  updateLineMarkers() {
    if (this.startMarker) this.startMarker.setMap(null);
    this.startMarker = RenderHelper.createStartMakerWithLabel(this.line, this.pathStore.editable);
    this.startMarker.setMap(this.map);

    if (this.endMarker) this.endMarker.setMap(null);
    this.endMarker = RenderHelper.createStopMakerWithLabel(this.line, this.pathStore.editable);
    this.endMarker.setMap(this.map);
  }

  updateLine(e: google.maps.MouseEvent) {
    if (!this.line) {
      this.createNewPolyline([]);
    }

    const newPath = this.line.getPath();
    if (newPath.getLength() == 0) {
      newPath.push(e.latLng);
    } else {
      const startPointCloser = this.isStartPointCloser(newPath.getAt(0), newPath.getAt(newPath.getLength() - 1), e.latLng);
      if (startPointCloser) {
        newPath.insertAt(0, e.latLng);
      } else {
        newPath.push(e.latLng);
      }
    }

    this.line.setPath(newPath);
    this.updateLineMarkers();
    this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
    this.pathStore.updateCurrentLine(this.line);
  }

  deletePointFromPath(vertexClicked: number) {
    if (this.workArea){
      const newPath = this.workArea.getPath();
      if (newPath.getLength() <= 3) return;

      newPath.removeAt(vertexClicked);
      this.workArea.setPath(newPath);
      this.updateWorkArea();
    }
    if (this.cropArea){
      const newPath = this.cropArea.getPath();
      if (newPath.getLength() <= 3) return;

      newPath.removeAt(vertexClicked);
      this.cropArea.setPath(newPath);
      this.updateCropArea();
    }
  }

  updateWorkAreaPolygon(e: google.maps.MouseEvent) {
    if (!this.workArea) {
      this.createNewWorkAreaPolygon([]);
    }
    this.addPointToWorkArea(e);
  }

  updateCropAreaPolygon(e: google.maps.MouseEvent) {
    if (!this.cropArea) {
      this.createNewCropAreaPolygon([]);
    }
    this.addPointToCropArea(e);
  }

  addPointToWorkArea(e: google.maps.MouseEvent) {
    if (!this.workArea) return;
    const newPath = this.workArea.getPath();
    if (newPath == undefined || newPath.getLength() == 0) {
      this.workArea.setPath([e.latLng]);
    } else {
      newPath.push(e.latLng);
      this.workArea.setPath(newPath);
    }

    this.updateWorkArea();
  }

  updateWorkArea() {
    if (!this.workArea) return;
    let area = 0;
    let currentWorkArea: google.maps.LatLng[] = [];

    if (this.workArea.getPath() != undefined) {
      currentWorkArea = this.workArea.getPath().getArray();
      area = this.googleApi.maps.geometry.spherical.computeArea(this.workArea.getPath());
    }

    this.pathStore.setCurrentWorkArea(currentWorkArea);
    this.pathStore.setSelectedWorkAreaArea(Math.round(area));
  }

  addPointToCropArea(e: google.maps.MouseEvent) {
    if (!this.cropArea) return;
    const newPath = this.cropArea.getPath();
    if (newPath == undefined || newPath.getLength() == 0) {
      this.cropArea.setPath([e.latLng]);
    } else {
      newPath.push(e.latLng);
      this.cropArea.setPath(newPath);
    }

    this.updateCropArea();
  }

  updateCropArea() {
    if (!this.cropArea) return;
    let area = 0;
    let currentCropArea: google.maps.LatLng[] = [];

    if (this.cropArea.getPath() != undefined) {
      currentCropArea = this.cropArea.getPath().getArray();
      area = this.googleApi.maps.geometry.spherical.computeArea(this.cropArea.getPath());
    }

    this.pathStore.setCurrentCropArea(currentCropArea);
    this.pathStore.setSelectedCropAreaArea(Math.round(area));
  }

  /**
   * Checks if the start point or end point is closest to the clicked point.
   * Returns true if the start point is closest to the clicked point.
   */
  isStartPointCloser(startPoint: google.maps.LatLng, endPoint: google.maps.LatLng, clickedPoint: google.maps.LatLng): boolean {
    const distanceToStart = Math.hypot(startPoint.lat() - clickedPoint.lat(), startPoint.lng() - clickedPoint.lng());
    const distanceToEnd = Math.hypot(endPoint.lat() - clickedPoint.lat(), endPoint.lng() - clickedPoint.lng());
    return distanceToStart < distanceToEnd;
  }

  /**
   * Returns the length of the path in meters
   */
  findPathLengthInMeters(path: google.maps.Polyline) {
    const points = path.getPath().getArray();
    const distance = GpsHelper.findPathLengthInMeters(points);
    return Math.round(distance);
  }
}
